import {
  PricingFilterSection,
  PricingFilterSectionKind,
  PricingFilterSelections,
  RangeFilterSection,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-types';

export enum PricingRangeMode {
  Currency = 'currency',
  Percent = 'percent',
}

// A real section of its own: own URL params, own store entry.
export const percentSectionId = (columnId: string): string => `${columnId}Pct`;

export interface PricingRangeTwins<T> {
  [PricingRangeMode.Currency]: RangeFilterSection<T>;
  [PricingRangeMode.Percent]: RangeFilterSection<T>;
}

export interface PricingFilterGroup<T> {
  id: string;
  section: PricingFilterSection<T>;
  twins?: PricingRangeTwins<T>; // "$ / %" switcher for the delta fields
}

const isPercentTwin = <T>(section: RangeFilterSection<T>): boolean =>
  section.id !== section.toggleGroupId;

export const groupFilterSections = <T>(
  sections: PricingFilterSection<T>[],
): PricingFilterGroup<T>[] => {
  const twinsByGroup = new Map<string, Partial<PricingRangeTwins<T>>>();

  for (const section of sections) {
    if (
      section.kind !== PricingFilterSectionKind.Range ||
      !section.toggleGroupId
    ) {
      continue;
    }
    const twins = twinsByGroup.get(section.toggleGroupId) ?? {};
    twins[
      isPercentTwin(section)
        ? PricingRangeMode.Percent
        : PricingRangeMode.Currency
    ] = section;
    twinsByGroup.set(section.toggleGroupId, twins);
  }

  const groups: PricingFilterGroup<T>[] = [];

  for (const section of sections) {
    const groupId =
      section.kind === PricingFilterSectionKind.Range
        ? section.toggleGroupId
        : undefined;

    if (!groupId) {
      groups.push({ id: section.id, section });
      continue;
    }

    const twins = twinsByGroup.get(groupId);
    // Half a pair renders plain rather than as a one-sided toggle.
    if (
      !twins?.[PricingRangeMode.Currency] ||
      !twins[PricingRangeMode.Percent]
    ) {
      groups.push({ id: section.id, section });
      continue;
    }

    if (isPercentTwin(section as RangeFilterSection<T>)) {
      continue;
    }

    const complete = twins as PricingRangeTwins<T>;
    groups.push({
      id: groupId,
      section: complete[PricingRangeMode.Currency],
      twins: complete,
    });
  }

  return groups;
};

const hasBound = (
  { ranges }: PricingFilterSelections,
  sectionId: string,
): boolean => {
  const bound = ranges[sectionId];
  return Boolean(bound && (bound.min !== null || bound.max !== null));
};

export const hasVaryingBounds = <T>(section: RangeFilterSection<T>): boolean =>
  section.bounds.max > section.bounds.min;

// Picks which twin to show, in priority order: an explicit toggle wins;
// otherwise a bound already selected (e.g. from a URL) opens whichever twin
// carries it; otherwise avoid a flat 0-0 slider by preferring whichever twin
// actually has a range to slide; otherwise default to currency.
export const resolveRangeMode = <T>(
  twins: PricingRangeTwins<T>,
  overrides: Record<string, PricingRangeMode>,
  selections: PricingFilterSelections,
): PricingRangeMode => {
  const override = overrides[twins[PricingRangeMode.Currency].id];
  if (override) {
    return override;
  }
  if (hasBound(selections, twins[PricingRangeMode.Percent].id)) {
    return PricingRangeMode.Percent;
  }
  if (hasBound(selections, twins[PricingRangeMode.Currency].id)) {
    return PricingRangeMode.Currency;
  }
  if (
    !hasVaryingBounds(twins[PricingRangeMode.Currency]) &&
    hasVaryingBounds(twins[PricingRangeMode.Percent])
  ) {
    return PricingRangeMode.Percent;
  }
  return PricingRangeMode.Currency;
};
